import { EventSummary } from '@/modules/Events/components/types'
import { convertDateToLocal, formatDateWithFormatter } from '@/utils/function'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowBack, ArrowForward, Typogaphy } from 'vnetwork-ui'

dayjs.extend(isoWeek)

interface CalendarProps {
    eventSummary?: EventSummary
    currentDate: Date
    setCurrentDate: (date: Date) => void
    selectedDate: Date
    setSelectedDate: (date: Date) => void
}
const Calendar: React.FC<CalendarProps> = ({
    currentDate,
    setCurrentDate,
    selectedDate,
    setSelectedDate,
    eventSummary,
}) => {
    const { t } = useTranslation()

    // Lấy ngày đầu tuần của selectedDate
    const selectedDayjs = dayjs(selectedDate)
    const weekStart = selectedDayjs.startOf('isoWeek') // Sunday = 0 (dayjs default) use isoWeek
    // const weekEnd = selectedDayjs.endOf('week')

    // Generate days in week using dayjs
    const daysInWeek = []
    for (let i = 0; i < 7; i++) {
        daysInWeek.push(weekStart.add(i, 'day').toDate())
    }

    // Function to check if a day has events
    const hasEvents = (day: Date): boolean => {
        // Format ngày hiện tại thành YYYY-MM-DD
        const dayString = formatDateWithFormatter(
            day.toLocaleString(),
            'YYYY-MM-DD'
        )
        // Tìm trong eventSummary.date array
        const dayData = eventSummary?.days?.find((eventDay) => {
            // Parse ISO date và format về YYYY-MM-DD để so sánh
            const eventDateString = convertDateToLocal(eventDay?.date)
            return eventDateString === dayString
        })
        return dayData?.event_count && dayData?.event_count > 0 ? true : false
    }

    //
    const goToPreviousDay = () => {
        const newDate = dayjs(currentDate).subtract(1, 'day').toDate()
        setCurrentDate(newDate)
        setSelectedDate(newDate)
    }
    const goToNextDay = () => {
        const newDate = dayjs(currentDate).add(1, 'day').toDate()
        setCurrentDate(newDate)
        setSelectedDate(newDate)
    }
    return (
        <div className="mb-4">
            <div className="flex justify-center items-center mb-3">
                <h4 className="font-semibold text-primary">
                    {t('month')}{' '}
                    {formatDateWithFormatter(
                        selectedDate.toLocaleString(),
                        'MM, YYYY'
                    )}
                </h4>
            </div>

            {/* Week Grid */}
            <div className="flex justify-center items-center mb-3">
                <div
                    className="flex items-center justify-center min-w-[34px] h-[34px] rounded-full
                    hover:bg-secondary-20 transition-colors
                "
                >
                    <ArrowBack
                        className="w-3 h-3 text-primary cursor-pointer"
                        onClick={goToPreviousDay}
                    />
                </div>
                <div className="w-full">
                    <div className="grid grid-cols-7 gap-1 text-center text-xs w-full">
                        {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map(
                            (day) => (
                                <div
                                    key={day}
                                    className="text-gray-500 p-1 font-medium"
                                >
                                    {day}
                                </div>
                            )
                        )}
                    </div>
                    <div className="grid grid-cols-7 gap-1 text-center text-xs w-full">
                        {daysInWeek.map((day: Date) => {
                            const dayDayjs = dayjs(day)
                            const selectedDateDayjs = dayjs(selectedDate)
                            const todayDayjs = dayjs()

                            const isSelected = dayDayjs.isSame(
                                selectedDateDayjs,
                                'day'
                            )
                            const isTodayDate = dayDayjs.isSame(
                                todayDayjs,
                                'day'
                            )
                            const isCurrentMonth = dayDayjs.isSame(
                                selectedDateDayjs,
                                'month'
                            )
                            const dayHasEvents = hasEvents(day)

                            return (
                                <div
                                    key={day.toString()}
                                    className="col-span-1 flex items-center justify-center"
                                >
                                    <div
                                        onClick={() => setSelectedDate(day)}
                                        className={`
                                        rounded-full hover:bg-secondary-20 w-[34px] h-[34px] transition-colors
                                        flex items-center justify-center cursor-pointer relative
                                        ${isTodayDate ? 'bg-primary text-white' : ''}
                                        ${isSelected && !isTodayDate ? 'bg-success hover:text-primary' : ''}
                                        ${!isCurrentMonth ? 'text-gray-300' : ''}
                                    `}
                                    >
                                        <Typogaphy className="text-sm font-medium">
                                            {formatDateWithFormatter(
                                                day.toLocaleString(),
                                                'D'
                                            )}
                                        </Typogaphy>
                                        {dayHasEvents &&
                                            !isTodayDate &&
                                            isCurrentMonth && (
                                                <div className="w-1 h-1 bg-primary rounded-full absolute top-1.5 right-1.5"></div>
                                            )}
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>
                <div
                    className="flex items-center justify-center min-w-[34px] h-[34px] rounded-full
                    hover:bg-secondary-20 transition-colors
                "
                >
                    <ArrowForward
                        className="w-3 h-3 text-primary cursor-pointer"
                        onClick={goToNextDay}
                    />
                </div>
            </div>
        </div>
    )
}

export default Calendar
